/**
 * Wiring. The only module that can see every subsystem, and the only one
 * allowed to.
 *
 * Frame shape:
 *   1. `Clock.advance` decides how many 1/60 steps this frame owes.
 *   2. Each step runs the sim in a fixed order — lifecycle, spawns, flock,
 *      enemies, towers, projectiles, grid, particles, trails.
 *   3. `world.fx` is drained: particles and shockwaves by the systems that
 *      already ingest them, shake into the camera and combat text into the DOM
 *      here.
 *   4. Every render system syncs against the interpolation factor, the frame is
 *      composited, and `clearFx` resets the queues for the next frame.
 *
 * Nothing below reads wall time except the clock itself, so the capture harness
 * can drive `advanceFrames` outside the animation loop and get the same pixels
 * every run.
 */

import * as THREE from 'three';

import { QUALITY, SIM, VIEW, WAVES } from '@/core/config';
import type { QualityTier } from '@/core/config';
import {
  forget, measure, rememberChoice, saveSample, storedVerdict, verdictFor,
} from '@/core/bench';
import type { BenchHost, BenchVerdict } from '@/core/bench';
import { perf } from '@/core/perf';
import { ROLE, emissive, roleHex } from '@/core/palette';
import type { PhosphorName } from '@/core/palette';
import type { Tower, UpgradePath, World } from '@/core/types';

import { Clock, clearFx, createWorld, shakeAmplitude } from '@/game/World';
import { Input } from '@/game/Input';
import { Session, createRoundDriver, usingStubWaves } from '@/game/Session';
import { installMap } from '@/game/Track';
import {
  ACTS, actLength, actOf, isActFinale, towersLockedAt,
} from '@/game/Levels';
import type { Ability, Act } from '@/game/Levels';
import { stepFlock } from '@/game/Flock';
import { enemyById } from '@/game/EnemyCatalog';
import { waveHpScaleFor } from '@/game/Waves';
import {
  acquire, resetEnemies, setCashForKill, spawnPending, stepEnemies, vaporiseAt,
} from '@/game/entities/Enemy';
import { haptic, leakIntensity, resetHaptics, setHapticsEnabled, setHapticsReducedMotion } from '@/core/haptics';
import type { EnemyInternal } from '@/game/entities/Enemy';

import { MAX_TIER, TOWERS, towerById } from '@/game/towers/Catalog';
import { EnemyIndex, cycleTargeting } from '@/game/towers/Targeting';
import { ProjectileSystem } from '@/game/towers/Projectiles';
import { TowerSystem, createTower, refreshTower } from '@/game/towers/TowerSystem';
import {
  applyUpgrades, collectGrants, maxTierFor, purchase, sellValue, totalInvested,
} from '@/game/towers/Upgrades';

import { Renderer } from '@/render/Renderer';
import { DefeatFx } from '@/render/DefeatFx';
import type { PostParams } from '@/render/PostStack';
import { WarpGrid } from '@/render/WarpGrid';
import { HydraBackground } from '@/render/HydraBackground';
import { ParticleSystem } from '@/render/ParticleSystem';
import { TrailSystem } from '@/render/TrailSystem';
import { EnemyRenderer } from '@/render/EnemyRenderer';
import { TowerRenderer } from '@/render/TowerRenderer';
import { PlacementOverlay } from '@/render/PlacementOverlay';

import {
  ActPanel, DEFAULT_SETTINGS, GameOver, HUD, PauseMenu, Settings, ShopPanel,
  Toast, Tooltip, TowerPanel, WaveBar, roman,
  LIVES_CRITICAL,
} from '@/ui';
import type { ActPanelState, ActView, GameOverState, SettingsValues } from '@/ui';
import { el, icon } from '@/ui';

import { audio, deathSfx, panForX, shootSfx } from '@/audio/Audio';
import type { SfxName } from '@/audio/Audio';

import * as scenarios from '@/dev/scenarios';

// ─────────────────────────────────────────────────────────────── the track ──

/**
 * The corridor, drawn as a translucent ribbon with two hot edges.
 *
 * No subsystem owns the track's appearance — `game/Track.ts` is pure geometry —
 * so the mesh is built here from the same samples the sim walks. Vertices are
 * generated once at map install and never touched again.
 */
class TrackView {
  readonly object = new THREE.Group();

  private readonly fill: THREE.Mesh;
  private readonly edges: THREE.LineSegments;
  private readonly ends: THREE.Points;

  constructor(world: World) {
    const P = world.track.points;
    const T = world.track.tangents;
    const W = world.track.width;
    const count = W.length;
    // One rib every 4px. Below that the ribbon costs vertices for a curvature
    // nobody can see at 640x360.
    const stride = 4;
    const ribs = Math.max(2, Math.floor((count - 1) / stride) + 1);

    const pos = new Float32Array(ribs * 2 * 3);
    const col = new Float32Array(ribs * 2 * 3);
    const idx: number[] = [];
    const edgePos = new Float32Array((ribs - 1) * 2 * 2 * 3);
    const edgeCol = new Float32Array((ribs - 1) * 2 * 2 * 3);

    const fillC = emissive(ROLE.track, 0.5);
    const edgeC = emissive(ROLE.trackEdge, 1.35);

    let e = 0;
    for (let r = 0; r < ribs; r++) {
      const i = Math.min(count - 1, r * stride);
      const x = P[i * 2];
      const y = P[i * 2 + 1];
      const nx = -T[i * 2 + 1];
      const ny = T[i * 2];
      const w = W[i];

      const a = r * 2;
      const b = a + 1;
      pos[a * 3] = x + nx * w; pos[a * 3 + 1] = y + ny * w;
      pos[b * 3] = x - nx * w; pos[b * 3 + 1] = y - ny * w;
      for (let k = 0; k < 3; k++) {
        col[a * 3 + k] = fillC[k];
        col[b * 3 + k] = fillC[k];
      }

      if (r > 0) {
        const pa = (r - 1) * 2;
        idx.push(pa, pa + 1, a, pa + 1, b, a);
        // Two edge segments per rib gap: left rail then right rail.
        for (const [from, to] of [[pa, a], [pa + 1, b]] as const) {
          for (const v of [from, to]) {
            edgePos[e * 3] = pos[v * 3];
            edgePos[e * 3 + 1] = pos[v * 3 + 1];
            for (let k = 0; k < 3; k++) edgeCol[e * 3 + k] = edgeC[k];
            e++;
          }
        }
      }
    }

    const fillGeo = new THREE.BufferGeometry();
    fillGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    fillGeo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    fillGeo.setIndex(idx);
    this.fill = new THREE.Mesh(fillGeo, new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      side: THREE.DoubleSide,
    }));
    this.fill.renderOrder = -60;
    this.fill.frustumCulled = false;

    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(edgePos, 3));
    edgeGeo.setAttribute('color', new THREE.BufferAttribute(edgeCol, 3));
    this.edges = new THREE.LineSegments(edgeGeo, new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    }));
    this.edges.renderOrder = -55;
    this.edges.frustumCulled = false;

    // Entry and exit, as two fat points the bloom turns into portals.
    const endPos = new Float32Array([P[0], P[1], 0, P[(count - 1) * 2], P[(count - 1) * 2 + 1], 0]);
    const inC = emissive(ROLE.spawnPortal, 2.4);
    const outC = emissive(ROLE.exitPortal, 2.4);
    const endGeo = new THREE.BufferGeometry();
    endGeo.setAttribute('position', new THREE.BufferAttribute(endPos, 3));
    endGeo.setAttribute('color', new THREE.BufferAttribute(
      new Float32Array([inC[0], inC[1], inC[2], outC[0], outC[1], outC[2]]), 3,
    ));
    this.ends = new THREE.Points(endGeo, new THREE.PointsMaterial({
      size: 9,
      sizeAttenuation: false,
      vertexColors: true,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
    }));
    this.ends.renderOrder = -54;
    this.ends.frustumCulled = false;

    this.object.add(this.fill, this.edges, this.ends);
  }

  dispose(): void {
    for (const m of [this.fill, this.edges, this.ends]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.object.clear();
  }
}

// ───────────────────────────────────────────────── floating combat text ──

/**
 * `world.fx.numbers` has no consumer anywhere in the render layer, so it gets
 * one here: a pooled DOM layer riding on top of the tube. Numbers are the one
 * piece of gameplay text that must stay legible at any output scale, and the
 * glyph atlas has no digits, so DOM is the honest answer rather than a
 * compromise.
 */
class FloatingNumbers {
  private readonly layer = el('div', 'fx-numbers');
  private readonly pool: HTMLElement[] = [];
  private readonly live: { node: HTMLElement; x: number; y: number; t: number }[] = [];

  /** Beyond this the screen is soup and the numbers stop being information. */
  private static readonly MAX_LIVE = 36;
  private static readonly LIFE = 0.85;

  constructor(host: HTMLElement) {
    this.layer.style.cssText =
      'position:absolute;inset:0;pointer-events:none;z-index:8;overflow:hidden;';
    host.append(this.layer);
  }

  push(x: number, y: number, value: number, crit: boolean): void {
    if (this.live.length >= FloatingNumbers.MAX_LIVE) return;
    const node = this.pool.pop() ?? this.make();
    const rounded = Math.round(value);
    node.textContent = rounded > 0 ? `${rounded}` : `${rounded}`;
    node.style.color = crit ? roleHex('critNumber') : roleHex('damageNumber');
    node.style.fontSize = crit ? '11px' : '9px';
    node.style.fontWeight = crit ? '700' : '400';
    node.hidden = false;
    this.layer.append(node);
    this.live.push({ node, x, y, t: 0 });
  }

  /** `scale` converts buffer pixels to layer pixels; `ox`/`oy` are the letterbox. */
  update(dt: number, scale: number, ox: number, oy: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const f = this.live[i];
      f.t += dt;
      if (f.t >= FloatingNumbers.LIFE) {
        f.node.remove();
        f.node.hidden = true;
        this.pool.push(f.node);
        this.live[i] = this.live[this.live.length - 1];
        this.live.pop();
        continue;
      }
      const k = f.t / FloatingNumbers.LIFE;
      const rise = 14 * k;
      f.node.style.left = `${ox + f.x * scale}px`;
      f.node.style.top = `${oy + (f.y - rise) * scale}px`;
      f.node.style.opacity = `${1 - k * k}`;
    }
  }

  clear(): void {
    for (const f of this.live) {
      f.node.remove();
      this.pool.push(f.node);
    }
    this.live.length = 0;
  }

  private make(): HTMLElement {
    const n = el('span', 'num');
    n.style.cssText =
      'position:absolute;transform:translate(-50%,-100%);white-space:nowrap;' +
      'text-shadow:0 0 4px currentColor;pointer-events:none;line-height:1;';
    return n;
  }
}

// ────────────────────────────────────────────────────────────────── boot ──

/** Family names the audio subsystem knows, keyed by tower archetype id. */
/**
 * How wide a finger's catch on a placed tower is, in CSS pixels.
 *
 * 44 is the WCAG 2.5.5 / Apple HIG floor the rest of this cabinet meets. It is
 * declared in CSS pixels rather than buffer pixels because a finger is a
 * physical object and the buffer is not: the same 640-pixel board is 372 CSS px
 * wide on a phone and 1600 on a desktop.
 */
const TOUCH_PICK_CSS = 44;

const SFX_FAMILY: Readonly<Record<string, string>> = {
  turret: 'dart', flak: 'cannon', pylon: 'gatling', laser: 'laser', tesla: 'tesla',
  mortar: 'mortar', cryo: 'frost', acid: 'acid', sniper: 'rail', singularity: 'singularity',
};

/** Fetches a required element, or says exactly which one the page is missing. */
function need<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`main: index.html is missing #${id}.`);
  return node as T;
}

function boot(): void {
  const canvas = need<HTMLCanvasElement>('gl');
  const hudHost = need('hud');
  const tube = need('tube');
  // Keyboard focus has to land somewhere that is not a button.
  canvas.tabIndex = 0;

  // ── world and map ──
  // The corridor belongs to the act, not to the file. Act I is where a run
  // starts, so that is what boots; `installActMap` swaps it at every boundary.
  const world = createWorld('pixel-td');
  let track = installMap(world, ACTS[0].map);

  // ── render core ──
  const renderer = new Renderer({ canvas, quality: 'ultra' });

  // Sits below the lattice and carries its own renderOrder. `enabled: false`
  // means the synth is never constructed at all, so there is no second WebGL
  // context on a machine that would not have coped with one.
  //
  // No settings toggle yet: `SettingsValues` is owned elsewhere while the
  // responsive pass is in flight. The layer defaults on and the performance
  // governor holds rung 1 over it, so it is the first thing dropped under
  // load — which is the safety that actually matters here.
  const hydra = new HydraBackground();
  /*
   * The death sequence: detonation, cascade, afterglow, then the sheet. It
   * borrows the post chain through the same public set/get the settings
   * sliders use, and hands the sheet its cue through `reveal`.
   */
  const defeatFx = new DefeatFx({
    post: renderer.post,
    vaporise: vaporiseAt,
    reveal: () => showGameOver(false),
    done: () => {},
  });
  // The renderer owns the tick. Driving `update()` by hand here produced a
  // single stale partial frame — a few disconnected blocks instead of a field.
  renderer.background = hydra;
  const grid = new WarpGrid();
  let trackView = new TrackView(world);
  const trails = new TrailSystem();
  const placement = new PlacementOverlay();
  const towerRenderer = new TowerRenderer();
  const enemyRenderer = new EnemyRenderer();
  const particles = new ParticleSystem({ scale: QUALITY.ultra.particleScale });

  // Back to front. Every object also carries a renderOrder, which is what the
  // sorter actually obeys; this order is the one a reader should see.
  renderer.scene.add(hydra.object);
  renderer.scene.add(grid.object);
  renderer.scene.add(trackView.object);
  // Ground marking: over the corridor ribbon, under everything that moves.
  renderer.scene.add(placement.object);
  renderer.scene.add(trails.object);
  renderer.scene.add(towerRenderer.group);
  renderer.scene.add(enemyRenderer.object);
  renderer.scene.add(particles.object);

  /**
   * Swaps the corridor at an act boundary.
   *
   * Four things are the map, and all four have to move together or the game
   * quietly disagrees with itself: the spline the enemies walk, the flow field
   * they steer by when they are knocked off it, the buildable mask the shop and
   * the overlay test against, and the ribbon the player sees. `Track`'s
   * constructor builds the first three and `writeInto` copies them into the
   * world; the ribbon is vertex data generated once at install, so it is thrown
   * away and rebuilt rather than edited.
   *
   * Towers are not this function's problem — `Session.beginAct` has already
   * refunded and cleared them by the time it is called. See `onActStart`.
   */
  function installActMap(name: string): void {
    if (track.name === name) return;
    track = installMap(world, name);
    renderer.scene.remove(trackView.object);
    trackView.dispose();
    trackView = new TrackView(world);
    renderer.scene.add(trackView.object);
    // The persistence buffer is holding an afterimage of a corridor that no
    // longer exists, and it decays over seconds rather than frames.
    renderer.clearHistory();
  }

  // ── simulation ──
  const index = new EnemyIndex();
  const projectiles = new ProjectileSystem(index);
  const towers = new TowerSystem(index, projectiles);

  // The projectile pool asks for ribbons; the trail system hands them out.
  projectiles.trails = {
    acquire: (colour: PhosphorName) => trails.acquire(colour, 3, 1.2),
    release: (slot: number) => trails.release(slot),
  };

  // `Enemy.stepEnemies` already walks `e.dots` and subtracts from hp. Leaving
  // the projectile system's own pass on burns every stack twice per step, which
  // is exactly what its `tickDots` flag exists to let this file decide. Cost of
  // this choice: damage-over-time no longer routes through `resolve`, so it
  // pays no cash. Owner E should take that back once the two agree on one pass.
  projectiles.tickDots = false;

  const session = new Session(world, createRoundDriver(), {
    onRoundStart(round) {
      audio.play('waveStart');
      audio.setMusicState(WAVES.bossRounds.includes(round as never) ? 'boss' : 'combat');
      audio.setMusicIntensity(Math.min(1, round / WAVES.count));
      // The round number is already on screen twice — HUD stat and wave bar.
      // The toast only earns its place when it announces something neither of
      // them can: that this one is a boss round.
      if (WAVES.bossRounds.includes(round as never)) {
        toast.push({ id: 'round', kind: 'wave', text: `Round ${round} — BOSS` });
      }
    },
    onRoundEnd(round) {
      audio.setMusicState('prep');
      toast.push({ id: 'cleared', kind: 'success', text: `Round ${round} cleared` });
      /*
       * The prep window that just opened is the one before the act ends. Said
       * here rather than when the round starts, because this is the moment the
       * player can still act on it — spend, reposition, send early — and the
       * intermission that follows is the thing they could not see coming.
       */
      if (isActFinale(round + 1)) {
        const act = actOf(round + 1);
        toast.push({
          id: 'finale', kind: 'wave',
          text: `Round ${round + 1} ends act ${roman(act.index)}. An ability choice follows.`,
        });
      }
    },
    onDefeat() {
      audio.play('defeat');
      audio.setMusicState('defeat');
      haptic('defeat');
      // The sequence plays at 1x whatever the round ran at — a death scene on
      // fast-forward is a receipt, not a moment. `holdDefeat` buys exactly the
      // sequence's length; `reveal` raises the sheet from inside it.
      world.speed = 1;
      // The panel drops now, not at the reveal 108 ticks later: the run is
      // over, its controls are dead, and a tower detail floating over the
      // collapse is the one thing on screen that has not noticed.
      select(null);
      session.holdDefeat(defeatFx.duration);
      defeatFx.trigger(world);
    },
    onVictory() {
      haptic('victory');
      audio.play('victory');
      audio.setMusicState('victory');
      showGameOver(true);
    },
    onReset() {
      defeatFx.cancel();
      // The saturation accumulator must not carry one run's trauma into the next.
      resetHaptics();
      grid.reset();
      particles.clear();
      trails.clear();
      floaters.clear();
      selected = null;
      armed = null;
      toast.clear();
      closeActPanel();
      // A new run starts with the shelf up. Whoever dismissed the bar did it
      // about a board that no longer exists.
      setBarDismissed(false);
      // A restart from act VII goes back to act I's corridor, not to whatever
      // the run happened to end on.
      installActMap(ACTS[0].map);
    },

    /**
     * The act is over and the run is held. Everything visible is decided here;
     * nothing changes on the board until the player has picked, because a map
     * that swapped behind the panel would mean the refund line was describing
     * something that had already happened.
     */
    onActEnd(finished, next) {
      audio.play('victory');
      audio.setMusicState('prep');
      showActPanel(finished, next);
    },

    /**
     * The pick is in. `beginAct` has already refunded the board at full price
     * and emptied `world.towers`, so this installs the new corridor onto a
     * board with nothing standing on it and hands the player a 24-second prep
     * window (`WAVES.actPrepSeconds`, applied by `WaveDirector.complete`) to
     * rebuild in.
     */
    onActStart(act, taken) {
      installActMap(act.map);
      clearBoard();
      closeActPanel();
      audio.play('upgrade');
      audio.setMusicState('prep');
      toast.push({
        id: 'act', kind: 'wave', text: `Act ${roman(act.index)} — ${act.name}`,
      });
      if (taken) {
        toast.push({ id: 'ability', kind: 'success', text: `${taken.name} online` });
      }
      if (act.unlocks.length > 0) {
        const names = act.unlocks.map((id) => towerById(id).name).join(', ');
        toast.push({ id: 'unlock', kind: 'info', text: `Now buildable: ${names}` });
      }
      refreshUi();
    },
  });

  // Kills are only visible from inside the enemy simulation, and `cashForKill`
  // is called exactly once per kill — which makes it the one honest hook for
  // the kill counter and the death cue.
  //
  // It returns 0 on purpose. `Projectiles.resolve` already credits every hit
  // point as it is removed, so paying the full pool again on death doubles the
  // player's income. Pops pay; dying does not.
  setCashForKill((e) => {
    session.noteKill();
    playThrottled(deathSfx(e.archetype.family), e.pos.x, 0.06);
    return 0;
  });

  // ── UI ──
  /*
   * The build bar: one floating plate over the board, sized to its content.
   *
   * It replaces a 232px rail pinned to an edge, and the trade is measurable
   * rather than aesthetic. Counted over the seven act maps there are 3 257
   * buildable tiles; the rail's footprint stands on 344 of them and this bar's
   * on 107.
   *
   * The position is measured too, not chosen. The corridors of all seven maps
   * sweep through the middle of the board and leave their spare ground at the
   * corners, so column 0 and column 39 are the two richest columns on the map
   * and the middle of the bottom edge is the poorest place a plate of this size
   * can stand: 107 tiles there against 143 on the bottom right, 157 on the top
   * centre and 168 on the bottom left. It is also where a thumb already is.
   *
   * Whatever the mask thinks, `main.ts` re-stamps it from this box's live rect
   * every frame — the bar is `fit-content` and an act that unlocks two towers
   * changes its footprint mid-run.
   */
  const bar = el('div', 'buildbar');
  hudHost.append(bar);

  const floaters = new FloatingNumbers(hudHost);
  const toast = new Toast();
  const tooltip = new Tooltip();

  const hud = new HUD({
    onSpeed: (next) => setSpeed(next),
    onTogglePause: () => session.togglePause(),
    onSettings: () => openSettings(true),
  });

  const waveBar = new WaveBar({
    onStartEarly: () => startRound(),
  });

  const shop = new ShopPanel({
    onSelect: (id) => {
      armed = id;
      // The tower panel is an opaque sheet over the shop. Arming from a hotkey
      // while one is open would leave the player aiming at a playfield they
      // cannot see the tray for.
      if (id !== null) select(null);
      audio.play(id === null ? 'uiClick' : 'uiHover');
      refreshUi();
    },
    onDrag: (id, clientX, clientY) => {
      // A drag out of the tray is the aiming gesture on touch, so the overlay
      // has to be live throughout it rather than only at the drop.
      cursorInside = input.toBuffer(clientX, clientY, cursor);
      cursorValid = placementLegal(id, cursor.x, cursor.y);
    },
    onPlace: (id, clientX, clientY) => {
      // `toBuffer` clamps, so a release past the tube edge used to build at
      // whichever border tile the clamp landed on — a tower dropped somewhere
      // the player never pointed. Off the canvas is a cancelled drag.
      if (!input.toBuffer(clientX, clientY, cursor)) {
        armed = null;
        refreshUi();
        return;
      }
      tryPlace(id, cursor.x, cursor.y);
    },
    onPreview: (entry) => {
      previewId = entry ? entry.id : null;
      refreshUi();
    },
  });

  const towerPanel = new TowerPanel({
    onUpgrade: (id, path) => buyUpgrade(id, path),
    onSell: (id) => sellTower(id),
    onTargeting: (id, mode) => {
      const t = findTower(id);
      if (!t) return;
      t.targeting = mode;
      audio.play('uiClick');
      refreshUi();
    },
    /*
     * Back pops one view; Close dismisses the panel.
     *
     * Both let the tower go, and that is not a detail. The detail view *is* the
     * selection — a panel that went back to BUILD while a range ring stayed lit
     * on the field would be showing a state nothing on screen accounts for, and
     * the player's next tap on the board would sell or upgrade a tower they
     * thought they had put down.
     *
     * What separates them is where the player lands. Back leaves the shelf up,
     * which is where somebody who just finished with one tower is going next.
     * Close folds the panel to its grab bar, which is the only control in the
     * cabinet that hands the whole board back — the point of a Close beside a
     * Back is that it is worth pressing for a reason Back is not.
     */
    onBack: () => {
      select(null);
      audio.play('uiClick');
    },
    onClose: () => {
      select(null);
      setBarDismissed(true);
      audio.play('uiClick');
    },
  });

  const pause = new PauseMenu({
    onResume: () => session.setPaused(false),
    onRestart: () => { session.start(); refreshUi(); },
    onSettings: () => openSettings(true),
    onQuit: () => { session.start(); refreshUi(); },
  });

  const actPanel = new ActPanel({
    onChoose: (id) => {
      audio.play('uiClick');
      // The session drives everything from here: it refunds the board, empties
      // it, flips to prep and calls back into `onActStart`.
      session.chooseAbility(id);
    },
  });

  const gameOver = new GameOver({
    onRetry: () => { gameOverOpen = false; session.start(); refreshUi(); },
    onCopySeed: (seed) => { void navigator.clipboard?.writeText(seed); },
  });

  const settings = new Settings({
    onChange: (values) => applySettings(values),
    onClose: () => openSettings(false),
    // Measures again, from inside a sheet that is already holding the run
    // (`openSettings` takes the modal hold), so nothing is happening on the
    // board while the probe renders its frames.
    onCalibrate: () => {
      audio.play('uiClick');
      remeasure(true);
    },
  });

  hud.mount(hudHost);
  waveBar.mount(hudHost);
  shop.mount(bar);
  // The tower panel is a sibling of the bar, not a child of it. Inside, it
  // inherited the bar's width and its `overflow: hidden`, which capped the
  // upgrade UI at the shelf's width — fine beside a 1920px playfield, unusable
  // on a phone where five 46px tier pips need 238px of content on their own.
  // Detached, one CSS rule turns it into a bottom sheet. See `.towerpanel`.
  towerPanel.mount(hudHost);
  toast.mount(hudHost);
  tooltip.mount(hudHost);
  pause.mount(tube);
  actPanel.mount(tube);
  gameOver.mount(tube);
  settings.mount(tube);

  shop.setCatalog(TOWERS);

  /**
   * Putting the whole bar away, and the one control that brings it back.
   *
   * `Close` on the tower detail means "no detail, no shelf" — the one control
   * in the cabinet that hands the board back whole. It used to fold the rail to
   * its title row, which is how the player ended up with a full-width bar
   * reading `BUILD` and a 12px chevron as the only way out of it. There is no
   * title row to fold to now, so the bar goes entirely and leaves a tab: a full
   * `--tap` square, wearing the shop mark, parked where the bar was, so the way
   * back is where the thing that left it was.
   *
   * Offered only where the bar floats. In portrait the deck is a row of the
   * cabinet's stack rather than a plate on the glass — the playfield above it is
   * bound by the window's width, so dismissing the deck buys bezel and no board
   * at all — and a control whose whole promise is "you get the game back" must
   * not be shown where it cannot keep it.
   */
  const barTab = el('button', 'buildbar__tab');
  barTab.type = 'button';
  barTab.append(icon('shapes', { size: 24, role: 'uiAccent', glow: 0.8 }));
  barTab.setAttribute('aria-label', 'Open the build bar');
  barTab.dataset.tip = 'Bring the build bar back.';
  barTab.hidden = true;
  barTab.addEventListener('click', () => {
    setBarDismissed(false);
    audio.play('uiClick');
  });
  hudHost.append(barTab);

  let barDismissed = false;
  function setBarDismissed(next: boolean): void {
    if (barDismissed === next) return;
    barDismissed = next;
    bar.hidden = next;
    barTab.hidden = !next;
    // The bar has just stopped or started standing on tiles. `refreshUi` rebuilds
    // the mask off live boxes every frame, but the capture harness stops the
    // frame loop, and a mask one press behind is the swallowed click this
    // project has removed twice.
    occlusionDirty = true;
    refreshUi();
  }

  /*
   * The bar changes size without the tube changing size: an act unlocks two
   * towers, the detail view pushes over the shelf, the hint line grows a
   * sentence. Every one of those moves the plate's footprint on the board, and
   * the `ResizeObserver` on `#tube` above cannot see any of them.
   */
  const barWatch = new ResizeObserver(() => { occlusionDirty = true; });
  barWatch.observe(bar);
  barWatch.observe(towerPanel.element);

  /**
   * The panel's current view, for `ui.css` and for the platform Back gesture.
   *
   * BUILD is the root and the tower detail is pushed over it, which is a real
   * navigation and so it gets a real history entry. On Android the hardware
   * back button and the edge swipe both mean "up one level", and without an
   * entry to eat they mean "leave the game" — a player who tapped a tower and
   * swiped back would have lost the run to a gesture that, everywhere else on
   * their phone, closes a detail sheet.
   *
   * The entry is only ever unwound when it is provably ours. `history.back()`
   * against somebody else's top entry is how a single-page game navigates
   * itself off the page, and the state tag is cheaper than being sure nothing
   * else will ever push.
   */
  let panelView: 'build' | 'tower' = 'build';
  let poppingBack = false;

  function setPanelView(next: 'build' | 'tower'): void {
    if (next === panelView) return;
    panelView = next;
    document.documentElement.dataset.panelView = next;
    if (next === 'tower') {
      try { history.pushState({ pxPanel: 'tower' }, ''); } catch { /* file:// */ }
      return;
    }
    // Left the detail some other way — Back, Close, Escape, a sold tower, a
    // restart. The entry we pushed on the way in has to go with it, or the next
    // back gesture would pop a view the player has already left.
    if (!poppingBack && (history.state as { pxPanel?: string } | null)?.pxPanel === 'tower') {
      try { history.back(); } catch { /* file:// */ }
    }
  }

  window.addEventListener('popstate', () => {
    // Pops one view rather than dismissing everything: the gesture means "up",
    // and up from the tower detail is the build shelf.
    if (panelView === 'tower') {
      poppingBack = true;
      select(null);
      poppingBack = false;
    }
  });

  towerPanel.setRules({
    canPurchase: (tiers, path) => {
      const t = tiers as [number, number, number];
      return t[path] < Math.min(MAX_TIER, maxTierFor(t, path));
    },
    sellValue: (view) => Math.floor(view.invested * 0.7),
  });

  // ── acts: the shelf, and the panel ──

  const TOWER_IDS: readonly string[] = TOWERS.map((t) => t.id);

  let lockedRound = -1;
  let lockedIds: ReadonlySet<string> = new Set();

  /**
   * Ids the shop must grey out for a round. Cached on the round, because
   * `refreshUi` runs every frame and `towersLockedAt` allocates a set.
   */
  /**
   * The round the *shelf* is drawn for, which is not always the round the board
   * is on.
   *
   * `beginAct` moves the board into the new act before the player has picked an
   * ability, so the two new towers appeared — and finished their reveal —
   * behind the intermission scrim that was busy announcing them. The shelf is
   * held at the last round the player could actually see it, so the reveal
   * plays into a visible panel the moment the modal closes.
   */
  let shelfRound = -1;

  function shelfLocked(): ReadonlySet<string> {
    if (!session.betweenActs) shelfRound = session.boardRound;
    return lockedFor(shelfRound < 0 ? session.boardRound : shelfRound);
  }

  function lockedFor(round: number): ReadonlySet<string> {
    if (round !== lockedRound) {
      lockedRound = round;
      lockedIds = towersLockedAt(round, TOWER_IDS);
    }
    return lockedIds;
  }

  /**
   * Where the run is inside its act, for the wave bar.
   *
   * Cached on the round: the schedule cannot change inside one, and `refreshUi`
   * runs sixty times a second. The two array lookups are cheap; the object is
   * not, and `ARCHITECTURE.md` is unambiguous about per-frame allocation.
   */
  let actViewRound = -1;
  let actViewCache: ActView | null = null;

  function actView(round: number): ActView {
    if (round === actViewRound && actViewCache) return actViewCache;
    const act = actOf(round);
    const next = ACTS[act.index] ?? null;
    actViewRound = round;
    actViewCache = {
      index: act.index,
      name: act.name,
      // Clamped, because `actOf` clamps: round 0 during a reset would otherwise
      // report round zero of twelve.
      round: Math.min(actLength(act), Math.max(1, round - act.first + 1)),
      length: actLength(act),
      finale: isActFinale(round),
      next: next
        ? { name: next.name, towers: next.unlocks.map((id) => towerById(id).name) }
        : null,
    };
    return actViewCache;
  }

  /** Last state handed to the act panel, so closing it does not need a new one. */
  let actState: ActPanelState | null = null;

  function showActPanel(finished: Act, next: Act): void {
    /*
     * The intermission refunds the board and empties it, so a detail panel left
     * open over the scrim is describing a tower that is about to stop existing —
     * and it is drawn at z-index 22 under a z-index 70 sheet, which is a stale
     * panel behind a modal, the shape that has bitten this project twice. Pop
     * the stack before the sheet goes up.
     */
    select(null);
    actState = {
      open: true,
      finished,
      next,
      offers: session.intermission?.offers ?? [],
      // The mark and the price come along: the intermission draws these as
      // cards now, and a tower is recognised on the shelf by its artwork long
      // before it is recognised by its name.
      unlocks: next.unlocks.map((id) => {
        const t = towerById(id);
        return { name: t.name, icon: t.icon, cost: t.cost };
      }),
      // Read before the pick. `beginAct` empties the board, so asking after it
      // would report zero and the panel would claim the refund was nothing.
      refunded: session.boardValue(),
    };
    actPanel.update(actState);
    refreshUi();
  }

  function closeActPanel(): void {
    if (!actState) return;
    actState = { ...actState, open: false };
    actPanel.update(actState);
    actState = null;
  }

  // ── mutable UI state ──
  /** Archetype id armed for placement, or null. */
  let armed: string | null = null;
  /** Archetype hovered in the shop, for a range preview before you commit. */
  let previewId: string | null = null;
  let selected: Tower | null = null;
  let settingsOpen = false;
  let gameOverOpen = false;
  /**
   * The finished run's numbers, frozen at the moment it ended.
   *
   * The modal used to be handed its whole state by `showGameOver` and nothing
   * else ever spoke to it, so `open: true` was the only value it was ever given
   * — a one-way door. TRY AGAIN cleared `gameOverOpen`, restarted the run
   * correctly underneath, and left the sheet standing over a game the player
   * could no longer reach. The flag is the truth now and `refreshUi` paints it,
   * the way `pause` has always worked.
   *
   * Frozen rather than read live because the modal outlives the run it
   * describes: `session.start()` fires on the same click that closes it, and a
   * sheet that re-reads `session.stats` would count the score down to zero
   * during its own exit.
   */
  type GameOverResult = Omit<GameOverState, 'open'>;
  let gameOverResult: GameOverResult | null = null;
  /** What the sheet was last told, so the paint happens on a change, not a frame. */
  let gameOverPainted: { result: GameOverResult; open: boolean } | null = null;
  /*
   * Settings persist, which until now they did not: every slider, toggle and
   * tier choice silently reset on reload, and only the boot benchmark kept a
   * memory. The blob is merged over DEFAULT_SETTINGS field by field, so a
   * value written before a setting existed simply takes the default — the
   * same absent-key contract Settings.clone() already promises.
   *
   * Merged by *type*, not just by key. `JSON.parse` proves the blob is JSON
   * and nothing else; a blob from a future version — or an edited one — can
   * hold `"quality":"garbage"`, which would reach `QUALITY[tier].particleScale`
   * and brick every boot until localStorage is cleared, or a string slider,
   * which would ride a NaN into the shader uniforms. A field only survives the
   * merge if it has the default's type (and, for the tier, names a real rung).
   */
  const SETTINGS_KEY = 'pixeltd.settings.v1';

  function mergeTyped<T extends object>(base: T, over: unknown): T {
    const out = { ...base };
    if (typeof over !== 'object' || over === null) return out;
    for (const key of Object.keys(base) as (keyof T)[]) {
      const v = (over as Record<string, unknown>)[key as string];
      const d = base[key];
      if (typeof v !== typeof d) continue;
      if (typeof v === 'number' && !Number.isFinite(v)) continue;
      out[key] = v as T[keyof T];
    }
    return out;
  }

  function loadSettings(): SettingsValues {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw === null) return { ...DEFAULT_SETTINGS };
      const p = JSON.parse(raw) as Partial<SettingsValues>;
      const merged = mergeTyped(DEFAULT_SETTINGS, p);
      merged.audio = mergeTyped(DEFAULT_SETTINGS.audio, p.audio);
      merged.crt = mergeTyped(DEFAULT_SETTINGS.crt, p.crt);
      if (!(merged.quality in QUALITY)) merged.quality = DEFAULT_SETTINGS.quality;
      return merged;
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  function storeSettings(values: SettingsValues): void {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(values));
    } catch {
      // Private mode or a full quota: the game plays on, it just forgets.
    }
  }

  let settingsValues: SettingsValues = loadSettings();
  const cursor = { x: 0, y: 0 };
  let cursorValid = false;
  let cursorInside = false;
  /**
   * A finger is holding the build cursor and has not committed yet.
   *
   * A mouse builds on press, because it has already been hovering the tile and
   * has seen the overlay say yes. A finger has seen nothing — the tile is under
   * the finger — so the press only *aims*, and the release builds. Between the
   * two the player can slide to a green tile and watch it turn.
   */
  let touchAiming = false;
  /** Combined placement mask, rebuilt per frame while armed. Never reallocated. */
  const placementMask = new Uint8Array(VIEW.cols * VIEW.rows);

  /**
   * Tiles the chrome is standing on, one byte per tile, non-zero means covered.
   *
   * The canvas is full-bleed under the wide rail, so the board genuinely
   * continues beneath the HUD. Three answers were available and two of them
   * are wrong: letting clicks fall through opaque plate builds towers nobody
   * can see, and letting the mask keep calling those tiles open is what turned
   * a blocked click into a silent no-op — the thing that read as broken. So
   * the chrome's footprint becomes a placement rule like any other. The
   * overlay paints it red, `placementLegal` refuses it, and a drag released
   * over the rail says "Cannot build there" instead of swallowing the tower.
   *
   * Measured off the live boxes rather than off a hand-written list of them.
   * The rail is drawn on a `rotateY` barrel, panels move at every breakpoint
   * and one of them is a drawer; a table of coordinates in here would be wrong
   * the first time `ui.css` changed, and wrong silently.
   */
  const occluded = new Uint8Array(VIEW.cols * VIEW.rows);
  let occlusionDirty = true;

  /**
   * Stamps every pointer-capturing box under `node` into `occluded`.
   *
   * Recursion follows the rule the browser's own hit test follows: a box with
   * `pointer-events: none` is not a target, but its children still are, so we
   * descend through it; a box that *is* a target owns everything inside it, so
   * we stamp it and stop. Stopping matters — descending into the rail would
   * reach a scrolled-out shop tile whose rect sits outside the plate clipping
   * it, and stamp tiles the rail does not cover.
   *
   * A tile counts as covered when its *centre* falls in the box — the point
   * `placementLegal` snaps to, and the point a click has to land on. The test
   * runs in client space, against the very numbers `getBoundingClientRect`
   * returned, rather than converting the box into buffer pixels first: the
   * round trip put a float error on the comparison and disagreed with the
   * browser on tiles whose centre sat exactly on a box edge. `EDGE` widens
   * each box so what disagreement is left lands on the conservative side,
   * where a boundary tile reads blocked rather than reading open and
   * swallowing the click.
   *
   * A full pixel, not a half. At half, a tile centre landing *exactly* on
   * `b.top - EDGE` is a tie, and a tie is decided by whichever way the float
   * noise fell — which is not a side. Measured at 844x390 with the wave bar
   * grown by its between-rounds SEND button: nine tile centres at cy 314.9
   * against a bar whose top was 315.4, so `cy < b.top - 0.5` came out 314.9 vs
   * 314.9 and the tiles went unstamped, while `elementFromPoint` at the same
   * coordinate returned the bar. Nine tiles the overlay painted green where
   * the click died in the band. Widening to 1 moves the boundary off the tie
   * and onto the side the comment above always claimed: a tile within a pixel
   * of a HUD box is refused, not swallowed. Tiles are 16.5 CSS px apart there,
   * so nothing but the boundary case changes.
   *
   * About forty rect reads. The same answer taken from `elementFromPoint` once
   * per tile measured 5.5ms — a third of a frame.
   */
  const EDGE = 1;

  function stampOcclusion(node: Element, rect: DOMRect): void {
    for (const child of node.children) {
      if (child === canvas) continue;
      const style = getComputedStyle(child);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      // The canvas's own ancestors capture, but only out in the letterbox
      // where there is no board to capture over.
      if (style.pointerEvents === 'none' || child.contains(canvas)) {
        stampOcclusion(child, rect);
        continue;
      }
      const b = child.getBoundingClientRect();
      if (b.width < 1 || b.height < 1) continue;
      if (b.right < rect.left || b.left > rect.right) continue;
      if (b.bottom < rect.top || b.top > rect.bottom) continue;
      for (let row = 0; row < VIEW.rows; row++) {
        const cy = rect.top + (((row + 0.5) * VIEW.tile) / VIEW.height) * rect.height;
        if (cy < b.top - EDGE || cy > b.bottom + EDGE) continue;
        for (let col = 0; col < VIEW.cols; col++) {
          const cx = rect.left + (((col + 0.5) * VIEW.tile) / VIEW.width) * rect.width;
          if (cx < b.left - EDGE || cx > b.right + EDGE) continue;
          occluded[row * VIEW.cols + col] = 1;
        }
      }
    }
  }

  function rebuildOcclusion(): void {
    occlusionDirty = false;
    occluded.fill(0);
    // The band's box, not the canvas's. The canvas shows the rendered region,
    // which is wider or taller than 640x360 on a window that is not 16:9, so
    // mapping tile centres through it would stamp the mask in the wrong places.
    const r = renderer.viewRect();
    if (r.width < 1 || r.height < 1) return;
    stampOcclusion(tube, r);
  }

  function occludedAt(bufferX: number, bufferY: number): boolean {
    if (occlusionDirty) rebuildOcclusion();
    const col = Math.floor(bufferX / VIEW.tile);
    const row = Math.floor(bufferY / VIEW.tile);
    if (col < 0 || row < 0 || col >= VIEW.cols || row >= VIEW.rows) return true;
    return occluded[row * VIEW.cols + col] !== 0;
  }

  /*
   * `refreshUi` rebuilds this every frame off a clean layout. The flag is for
   * the two paths that can run between frames — a pointer move, and a resize
   * that relaid the whole cabinet out without any game state changing to hang
   * the rebuild off.
   */
  const occlusionWatch = new ResizeObserver(() => { occlusionDirty = true; });
  occlusionWatch.observe(tube);

  // ── input ──
  const input = new Input(canvas, {
    onGesture: () => { void audio.init().then(() => audio.setMusicState('prep')); },
    onPointerMove: (p) => {
      cursorInside = p.inside;
      cursor.x = p.x;
      cursor.y = p.y;
      cursorValid = placementLegal(armed, p.x, p.y);
    },
    onPointerDown: (p, button) => {
      if (button === 2) {
        if (armed !== null) { armed = null; refreshUi(); return; }
        select(null);
        return;
      }
      if (button !== 0) return;
      if (armed !== null) {
        // Touch aims now and builds on release; a mouse has already aimed.
        if (p.touch) { touchAiming = true; return; }
        tryPlace(armed, p.x, p.y);
        return;
      }
      if (!p.touch) select(towerAt(p.x, p.y, false));
    },
    onPointerUp: (p, button) => {
      if (button !== 0) return;
      if (touchAiming) {
        touchAiming = false;
        if (armed !== null) tryPlace(armed, p.x, p.y);
        return;
      }
      // A tap that armed nothing is a selection, resolved on release so that
      // sliding off a tower before lifting cancels it the way a button does.
      if (p.touch && armed === null && p.inside) select(towerAt(p.x, p.y, true));
    },
    onPointerCancel: () => {
      touchAiming = false;
      cursorInside = false;
    },
    onPointerLeave: () => { cursorInside = false; },
    onSelectSlot: (i) => {
      /*
       * The nth *visible* tile, not the nth archetype.
       *
       * The shelf now hides what the act has not unlocked, so indexing the
       * catalogue armed a tower with no tile — and the toast that explained
       * why named the act it arrives in, which is the same thing the greyed
       * tiles used to leak. A key with nothing behind it is a soft no.
       */
      const id = shop.visibleIds[i];
      if (id === undefined) { audio.play('uiDeny'); return; }
      armed = armed === id ? null : id;
      if (armed !== null) select(null);
      audio.play('uiHover');
      refreshUi();
    },
    onEscape: () => {
      if (gameOverOpen) return;
      if (settingsOpen) { openSettings(false); return; }
      if (armed !== null) { armed = null; refreshUi(); return; }
      if (selected !== null) { select(null); return; }
      session.togglePause();
      refreshUi();
    },
    onStartRound: () => startRound(),
    onCycleTargeting: (dir) => {
      if (!selected) return;
      selected.targeting = cycleTargeting(selected.targeting, dir);
      audio.play('uiClick');
      refreshUi();
    },
    onSpeed: (delta) => {
      const i = SIM.speeds.indexOf(world.speed as never);
      const next = SIM.speeds[Math.max(0, Math.min(SIM.speeds.length - 1, (i < 0 ? 0 : i) + delta))];
      setSpeed(next);
    },
    onTogglePause: () => { session.togglePause(); refreshUi(); },
    onSell: () => { if (selected) sellTower(selected.id); },
    onUpgrade: (path) => { if (selected) buyUpgrade(selected.id, path); },
  });
  input.attach();

  // ── placement and selection ──

  function findTower(id: number): Tower | null {
    for (const t of world.towers) if (t.id === id) return t;
    return null;
  }

  /**
   * The tower under a point, or the nearest one close enough to have been meant.
   *
   * `touch` widens the catch to 44 CSS pixels across, which is the floor every
   * other control in this cabinet already meets. The radius is otherwise in
   * *buffer* pixels, and that is what made this the worst target in the build:
   * a 390px phone draws 640 buffer pixels into 372 CSS ones, so the old
   * `max(10, …)` was 11.6 CSS px of diameter. Measured, the last offset from a
   * tower's centre that still selected it was 4px. A miss returns null, which
   * closes the panel the player already had open, so every trip into the
   * upgrade screen was a coin flip that punished losing.
   *
   * A mouse keeps the old radius exactly. It has a pixel-accurate pointer and a
   * hover state, and widening it there would start grabbing towers nobody
   * pointed at. `towerAt` returns the *nearest* candidate inside the radius, so
   * the wider touch catch degrades into "the closer of two" rather than into
   * "whichever came first in the array".
   */
  function towerAt(x: number, y: number, touch = false): Tower | null {
    let floor = 10;
    if (touch) {
      const box = renderer.viewRect();
      const cssPerBuffer = box.width / VIEW.width;
      if (cssPerBuffer > 0) floor = Math.max(floor, TOUCH_PICK_CSS / 2 / cssPerBuffer);
    }
    let best: Tower | null = null;
    let bestD2 = Infinity;
    for (const t of world.towers) {
      const dx = t.pos.x - x;
      const dy = t.pos.y - y;
      const d2 = dx * dx + dy * dy;
      const r = Math.max(floor, t.archetype.footprint + 2);
      if (d2 <= r * r && d2 < bestD2) { bestD2 = d2; best = t; }
    }
    return best;
  }

  /** Tile centre for a buffer point. Towers never stand off-grid. */
  function snap(v: number): number {
    return (Math.floor(v / VIEW.tile) + 0.5) * VIEW.tile;
  }

  function placementLegal(id: string | null, x: number, y: number): boolean {
    if (id === null) return false;
    // The unlock schedule is a placement rule, not a shop decoration. Testing it
    // here means the overlay, the ghost and the build all read the same answer,
    // so a hotkey cannot arm something the tray has greyed out.
    if (lockedFor(session.boardRound).has(id)) return false;
    const sx = snap(x);
    const sy = snap(y);
    if (sx < 0 || sy < 0 || sx >= VIEW.width || sy >= VIEW.height) return false;
    // Chrome standing on the tile is a placement rule, tested here beside the
    // corridor so the overlay, the ghost, the build and the drag-release all
    // give the player the same answer.
    if (occludedAt(sx, sy)) return false;
    if (!track.buildableAt(sx, sy)) return false;
    const arch = towerById(id);
    for (const t of world.towers) {
      const dx = t.pos.x - sx;
      const dy = t.pos.y - sy;
      const min = arch.footprint + t.archetype.footprint;
      if (dx * dx + dy * dy < min * min) return false;
    }
    return true;
  }

  /**
   * Rebuilds the per-tile placement mask for the armed archetype.
   *
   * `world.buildable` only knows about the corridor. The other two thirds of
   * the rule are stamped in here: that two footprints may not overlap, which
   * depends on which tower is armed, and that the HUD may not be standing on
   * the tile, which depends on the viewport. Neither belongs in the map's mask.
   * The test is the same one `placementLegal` runs, against the same snapped
   * tile centres, so a tile the overlay paints green is a tile that will accept
   * the build — and a tile it paints red is one the player is being told about
   * rather than one that quietly ignores them.
   */
  function buildPlacementMask(arch: { footprint: number }): void {
    if (occlusionDirty) rebuildOcclusion();
    placementMask.set(world.buildable);
    for (let i = 0; i < placementMask.length; i++) if (occluded[i]) placementMask[i] = 0;
    for (const t of world.towers) {
      const min = arch.footprint + t.archetype.footprint;
      const c0 = Math.max(0, Math.floor((t.pos.x - min) / VIEW.tile));
      const c1 = Math.min(VIEW.cols - 1, Math.floor((t.pos.x + min) / VIEW.tile));
      const r0 = Math.max(0, Math.floor((t.pos.y - min) / VIEW.tile));
      const r1 = Math.min(VIEW.rows - 1, Math.floor((t.pos.y + min) / VIEW.tile));
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const dx = t.pos.x - (c + 0.5) * VIEW.tile;
          const dy = t.pos.y - (r + 0.5) * VIEW.tile;
          if (dx * dx + dy * dy < min * min) placementMask[r * VIEW.cols + c] = 0;
        }
      }
    }
  }

  /**
   * Pushes the build cursor into the two systems that draw it.
   *
   * Affordability is folded into the same `valid` bit as legality on purpose.
   * "Not enough cash" and "not on the track" are the same answer to the same
   * question, and splitting them across two channels would mean a tile that
   * looks buildable and is not.
   */
  function syncPlacement(): void {
    const ghost = towerRenderer.ghost;
    const state = placement.state;
    if (armed === null) {
      ghost.active = false;
      state.armed = false;
      state.hasCursor = false;
      return;
    }

    const arch = towerById(armed);
    buildPlacementMask(arch);
    placement.setMask(placementMask);

    const sx = snap(cursor.x);
    const sy = snap(cursor.y);
    // Recomputed here rather than trusted from the last pointer event: arming
    // with a hotkey moves no pointer, and a tower built or sold under a resting
    // cursor changes the answer without one either.
    cursorValid = placementLegal(armed, cursor.x, cursor.y);
    const valid = cursorValid && world.cash >= arch.cost;

    state.armed = true;
    state.hasCursor = cursorInside;
    state.cursorX = sx;
    state.cursorY = sy;
    state.valid = valid;
    state.footprint = Math.max(VIEW.tile / 2, arch.footprint);

    ghost.active = cursorInside;
    ghost.archetypeId = arch.id;
    ghost.x = sx;
    ghost.y = sy;
    ghost.colour = arch.colour;
    ghost.blocked = !valid;
    ghost.range = arch.base.range;
  }

  function tryPlace(id: string, x: number, y: number): void {
    /*
     * Measure the chrome now, not on the last frame's word for it.
     *
     * `refreshUi` rebuilds the mask once a frame, which is enough for the
     * overlay and not enough for this: `ui/HUD.ts` rolls the cash readout on a
     * `requestAnimationFrame` of its own, so the pill widens digit by digit
     * outside the game's loop entirely and shoves the round pill sideways over
     * tiles the mask still calls open. One rebuild per click — a few dozen rect
     * reads, once, on the one event where being a frame wrong means either
     * ignoring the player or burying a tower under the HUD.
     */
    rebuildOcclusion();
    const arch = towerById(id);
    if (lockedFor(session.boardRound).has(id)) {
      // Unreachable through the UI now that the shelf hides what is locked and
      // the hotkeys index the shelf. Kept as the backstop it always was, minus
      // the sentence that named the act — nothing should be announcing a tower
      // the player has not been shown.
      audio.play('uiDeny');
      toast.push({
        id: 'locked', kind: 'warn',
        text: `${arch.name} is not on the shelf yet`,
      });
      armed = null;
      refreshUi();
      return;
    }
    if (!placementLegal(id, x, y)) {
      audio.play('uiDeny');
      toast.push({ id: 'blocked', kind: 'warn', text: 'Cannot build there' });
      return;
    }
    if (world.cash < arch.cost) {
      audio.play('uiDeny');
      toast.push({ id: 'poor', kind: 'error', text: 'Not enough cash', amount: arch.cost });
      return;
    }
    const t = createTower(arch, snap(x), snap(y));
    world.cash -= arch.cost;
    world.towers.push(t);
    session.noteTowerBuilt();
    audio.play('place', { x: t.pos.x });
    // A placement that does not shove the lattice reads as a decal.
    world.fx.shockwaves.push({ x: t.pos.x, y: t.pos.y, strength: 34, radius: 58, duration: 0 });
    armed = null;
    select(t);
  }

  function sellTower(id: number): void {
    const t = findTower(id);
    if (!t) return;
    const refund = sellValue(t);
    world.cash += refund;
    const i = world.towers.indexOf(t);
    if (i >= 0) world.towers.splice(i, 1);
    if (selected === t) select(null);
    audio.play('sell', { x: t.pos.x });
    toast.push({ id: 'sold', kind: 'cash', text: 'Sold', amount: refund });
    refreshUi();
  }

  function buyUpgrade(id: number, path: UpgradePath): void {
    const t = findTower(id);
    if (!t) return;
    const spent = purchase(t, path, world.cash);
    if (spent <= 0) {
      audio.play('uiDeny');
      return;
    }
    world.cash -= spent;
    refreshTower(t);
    audio.play('upgrade', { x: t.pos.x });
    refreshUi();
  }

  function select(t: Tower | null): void {
    if (selected === t) return;
    if (selected) selected.selected = false;
    selected = t;
    if (selected) {
      selected.selected = true;
      audio.play('uiClick', { x: selected.pos.x });
    }
    refreshUi();
  }

  // ── controls ──

  function setSpeed(next: number): void {
    world.speed = next;
    audio.play('uiClick');
    refreshUi();
  }

  function startRound(): void {
    const bonus = session.startEarly();
    if (bonus > 0) toast.push({ id: 'early', kind: 'cash', text: 'Sent early', amount: bonus });
  }

  /**
   * Raises or drops the settings sheet, and holds the run while it is up.
   *
   * Suspending input was never enough. The waves kept coming behind the scrim,
   * so pulling the volume down mid-round cost lives, and a long enough look at
   * the CRT sliders lost the run outright — which is also how the defeat sheet
   * came to be raised *behind* this one. A menu is not a difficulty setting.
   *
   * The hold is the same one the pause key takes, keyed differently so that
   * opening Settings from inside the pause menu and closing it again returns to
   * a paused game rather than resuming a run the player never asked to resume.
   */
  function openSettings(open: boolean): void {
    settingsOpen = open;
    settings.update({ open, values: settingsValues });
    tooltip.update({ enabled: !open });
    input.suspended = open;
    session.setHold('modal', open);
  }

  function showGameOver(victory: boolean): void {
    /*
     * Settings now holds the clock (see `openSettings`), so a run can no longer
     * end while it is up and this is a backstop rather than the fix it started
     * as. It stays because the two sheets still collide if one ever does: both
     * are `.scrim`s at z-index 70 and Settings is mounted second, so it wins the
     * tie and the defeat sheet is raised behind it — measured at 390x844 the
     * element at TRY AGAIN's centre was `input`, and at 1392x769 it was
     * Settings' own box. Escape is dead there too, because the key lands on the
     * game-over scrim, whose `onDismiss` is deliberately null.
     *
     * Nothing is lost by closing it. Every control applies live through
     * `onChange`, and the values it was editing live in `settingsValues`.
     */
    if (settingsOpen) openSettings(false);
    // And the tower detail, for the same reason it closes Settings: the run is
    // over, the board behind the sheet is finished, and a panel still offering
    // upgrades for a tower on it is the one thing on screen that has not
    // noticed.
    select(null);
    gameOverResult = {
      victory,
      round: world.round,
      totalRounds: WAVES.count,
      kills: session.stats.kills,
      cashEarned: session.stats.cashEarned,
      livesLost: session.stats.livesLost,
      // What the high score's third term is made of. Read off the world rather
      // than derived from `livesLost`, which would need the starting count and
      // would be wrong the day anything else spends a life.
      livesRemaining: Math.max(0, world.lives),
      towersBuilt: session.stats.towersBuilt,
      duration: session.stats.duration,
      seed: session.seed,
    };
    gameOverOpen = true;
    refreshUi();
  }

  function applySettings(values: SettingsValues): void {
    // A tier the player picked by hand outranks the boot probe, on this load and
    // on every load after it. Compared before the assignment, because this is
    // called for every volume nudge and only a *change* of tier is a choice.
    if (values.quality !== settingsValues.quality && !calibrating) {
      rememberChoice(gpuName, renderer.stats.hdr, values.quality);
    }
    settingsValues = values;
    storeSettings(values);
    renderer.setQuality(values.quality);
    particles.density = QUALITY[values.quality].particleScale;
    // Off keeps the layer's shader and target uncreated. The governor still
    // holds rung 1 over it, so a player leaving this on never costs frames —
    // it is dropped first under pressure regardless.
    hydra.setEnabled(values.hydra !== false);
    setHapticsEnabled(values.haptics !== false);
    setHapticsReducedMotion(values.reducedMotion === true);

    audio.setBus('master', values.audio.master);
    audio.setBus('music', values.audio.music);
    audio.setBus('sfx', values.audio.sfx);

    // Every CRT slider is a 0..2 multiplier over the tuned default.
    const base = renderer.post;
    const c = values.crt;
    base.setAll({
      scanlineStrength: defaults.scanlineStrength * c.scanlines,
      maskStrength: defaults.maskStrength * c.mask,
      curvatureX: defaults.curvatureX * c.curvature,
      curvatureY: defaults.curvatureY * c.curvature,
      intensity: defaults.intensity * c.bloom,
      persistence: defaults.persistence * c.persistence,
      chromaticAberration: defaults.chromaticAberration * c.aberration,
      noise: defaults.noise * c.noise,
      vignette: defaults.vignette * c.vignette,
    });
  }
  const defaults = renderer.post.snapshot();

  // ── audio helpers ──

  const sfxAt = new Map<string, number>();
  /** Rate-limits a cue so a forty-body swarm death does not become one click. */
  function playThrottled(name: SfxName, x: number, minGap: number): void {
    const now = world.time;
    const last = sfxAt.get(name) ?? -999;
    if (now - last < minGap) return;
    sfxAt.set(name, now);
    audio.play(name, { pan: panForX(x) });
  }

  const towerFlash = new WeakMap<Tower, number>();
  function fireSounds(): void {
    for (const t of world.towers) {
      const was = towerFlash.get(t) ?? 0;
      towerFlash.set(t, t.flash);
      if (t.flash > 0.9 && was <= 0.9) {
        playThrottled(shootSfx(SFX_FAMILY[t.archetype.id] ?? 'dart'), t.pos.x, 0.045);
      }
    }
  }

  // ── the loop ──

  const clock = new Clock();
  let captureMode = false;
  /** Presented frames since boot, counted only until the probe has had its turn. */
  let bootFrames = 0;
  let raf = 0;
  let lastBossHaptic: string | null = null;
  let lastLives = world.lives;
  let shakeX = 0;
  let shakeY = 0;

  /**
   * Re-scales bodies that the spawner just created.
   *
   * `Enemy.acquire` calls `waveHpScale(round)`, which takes the round and
   * nothing else, so one curve has to serve both trash and bosses — and it
   * cannot. `EnemyCatalog` authors bosses as absolute end-game statlines and
   * everything else as round-1 bases, so a single curve leaves round 100's boss
   * at a few percent of its intended share of the round.
   *
   * The real fix is widening `setWaveHpScale` to `(round, archetypeId)` in
   * `entities/Enemy.ts` and passing `p.archetypeId` from `spawnPending`; that
   * file belongs to another owner. Until then this corrects the tail of the
   * array in place. Trash costs one float compare and falls straight through.
   */
  function rescaleSpawns(from: number): void {
    const list = world.enemies;
    for (let i = from; i < list.length; i++) {
      const e = list[i] as EnemyInternal;
      const want = waveHpScaleFor(world.round, e.archetype.id);
      if (Math.abs(want - e.hpScale) < 1e-6) continue;
      const a = e.archetype;
      e.hpScale = want;
      e.maxHp = Math.max(1, Math.round(a.hp * want));
      e.hp = e.maxHp;
      e.shield = a.shield > 0 ? Math.round(a.shield * want) : 0;
    }
  }

  function simStep(): void {
    const dt = SIM.dt;
    world.time += dt;

    session.step(dt);
    const spawnedFrom = world.enemies.length;
    spawnPending(world);
    rescaleSpawns(spawnedFrom);
    stepFlock(dt, world, track);
    stepEnemies(dt, world, track);
    towers.step(dt, world);
    projectiles.step(dt, world);
    defeatFx.step(dt, world);
    grid.step(dt, world);
    particles.step(dt, world);
    trails.step(dt, world);
  }

  /**
   * Drains what nothing else drains, syncs every render system and composites.
   *
   * Particles and shockwaves are ingested by `ParticleSystem.step` and
   * `WarpGrid.step` from inside the sim step; what is left over is the shake
   * and the combat text, and both are pulled here before `clearFx` wipes them.
   */
  function renderFrame(alpha: number, frameDt: number): void {
    // Screen shake into the camera. The offset is snapped so the shake never
    // introduces the sub-pixel drift the whole pixel discipline exists to avoid.
    // Saturating, not the raw trauma sum — see `shakeAmplitude` in World.ts.
    const amp = shakeAmplitude(world);
    if (amp > 0.01) {
      shakeX = Math.round((world.rng() * 2 - 1) * amp);
      shakeY = Math.round((world.rng() * 2 - 1) * amp);
    } else {
      shakeX = 0;
      shakeY = 0;
    }
    renderer.camera.position.x = shakeX;
    renderer.camera.position.y = -shakeY;

    for (const n of world.fx.numbers) floaters.push(n.x, n.y, n.value, n.crit);

    // Before the tower sync: it is what fills the ghost the tower meshes draw.
    syncPlacement();

    grid.sync(world, alpha);
    trails.sync(world, alpha);
    placement.sync(world, alpha);
    towerRenderer.sync(world, alpha);
    enemyRenderer.sync(world, alpha);
    particles.sync(world, alpha);

    renderer.render(alpha);
    clearFx(world);

    const rect = renderer.viewRect();
    const host = hudHost.getBoundingClientRect();
    const scale = rect.width / VIEW.width;
    floaters.update(frameDt, scale, rect.left - host.left, rect.top - host.top);
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    if (captureMode) return;

    /*
     * The boot probe, on the second frame.
     *
     * Here rather than in the boot sequence so that the first frame reaches the
     * glass on time: this runs after frame one has been presented and before
     * frame two is built, which is the earliest moment a measurement costs the
     * player nothing they can see. `Clock.advance` below then charges the probe's
     * milliseconds as one wide frame, which it clamps like any other.
     */
    if (measureOwed && ++bootFrames >= 2) remeasure(false);

    /*
     * `session.held` is the whole answer: the pause key, any modal that covers
     * the board, the act intermission, and a finished run all arrive here as one
     * bit. Anything that wants the board to stop takes a hold on the session
     * rather than growing a second flag this line has to remember to read.
     *
     * The clock is advanced either way, at speed 1, and that is deliberate.
     * `Clock.advance` moves `last` to `now` on entry, so a held frame consumes
     * its own wall time instead of banking it — the accumulator drains through
     * the same loop and the steps are thrown away. Release after a minute in the
     * settings sheet therefore owes exactly one frame, not a minute of catch-up.
     * Skipping the call is what would corrupt it: `last` would sit at the moment
     * of the pause and the next `advance` would see a minute-wide frame, clamp
     * it to the 0.25s guard, and still fire a burst.
     */
    const speed = session.held ? 0 : world.speed;
    const steps = clock.advance(now, speed || 1);
    if (speed > 0) {
      for (let i = 0; i < steps; i++) simStep();
    }

    fireSounds();
    if (world.lives < lastLives) {
      playThrottled('leak', VIEW.width * 0.5, 0.2);
      /*
       * One pattern per event. At the critical crossing the double-pulse
       * replaces the leak's tick — two patterns back to back read as one long
       * buzz. And the killing leak says nothing here: onDefeat fired this
       * frame, and its collapse is the pattern that should land.
       */
      if (world.lives > 0) {
        if (world.lives <= LIVES_CRITICAL && lastLives > LIVES_CRITICAL) {
          haptic('livesCritical');
        } else {
          haptic('leak', leakIntensity(lastLives - world.lives));
        }
      }
      lastLives = world.lives;
    } else if (world.lives > lastLives) {
      lastLives = world.lives;
    }

    audio.setEnemyPressure(Math.min(1, world.enemies.length / 120));

    refreshUi();
    renderFrame(speed > 0 ? clock.alpha : 0, Math.min(0.05, steps * SIM.dt) || 1 / 60);
  }

  // ── UI refresh ──

  function refreshUi(): void {
    const view = session.view();
    if (view.boss !== lastBossHaptic) {
      if (view.boss !== null) haptic('bossArrival');
      lastBossHaptic = view.boss;
    }

    hud.update({
      cash: Math.floor(world.cash),
      lives: world.lives,
      round: world.round,
      totalRounds: WAVES.count,
      speed: world.speed,
      paused: session.paused,
    });

    waveBar.update({
      round: world.round,
      totalRounds: WAVES.count,
      prep: view.prep,
      progress: view.progress,
      prepRemaining: view.prepRemaining,
      incoming: view.incoming,
      boss: view.boss,
      canStartEarly: view.canStartEarly,
      earlyBonus: view.earlyBonus,
      /*
       * The round the board is about to see, not the one behind it.
       *
       * During prep `world.round` is the round just cleared — SEND starts
       * `round + 1`, and the progress track beside it is already counting that
       * window down. An act readout a round behind would mark the finale only
       * once the finale was being played, which is precisely the warning this
       * exists to give.
       */
      act: actView(view.prep ? world.round + 1 : world.round),
    });

    /*
     * Which view of the one panel is up, published where a stylesheet can read
     * it.
     *
     * Derived rather than stored. `selected` is the only fact — the detail view
     * exists because a tower is selected and for no other reason — and a second
     * copy of that in a `view` field is the copy that goes stale on the two
     * paths which clear the selection without going through `select()`
     * (`onReset` and `clearBoard`, both of which wipe the board out from under
     * an open panel). Read every frame off the truth, written only when it
     * turns over.
     */
    setPanelView(selected !== null ? 'tower' : 'build');

    shop.update({
      cash: Math.floor(world.cash),
      selected: armed,
      locked: shelfLocked(),
      covered: selected !== null,
    });

    towerPanel.update({
      tower: selected,
      cash: Math.floor(world.cash),
    });

    // An intermission holds the run in `paused` too, and two stacked modals with
    // a Resume button behind the one that owes the player a pick is the worst of
    // both.
    pause.update({
      open: session.paused && !settingsOpen && !session.betweenActs,
      round: world.round,
      cash: Math.floor(world.cash),
      lives: world.lives,
    });

    /*
     * The end-of-run sheet, painted from the flag rather than from the event
     * that raised it. Guarded on a change because the six score rows are
     * `textContent` writes and this runs every frame for the rest of the
     * session once a run has ended.
     */
    if (gameOverResult
      && (gameOverPainted?.result !== gameOverResult || gameOverPainted.open !== gameOverOpen)) {
      gameOverPainted = { result: gameOverResult, open: gameOverOpen };
      gameOver.update({ ...gameOverResult, open: gameOverOpen });
    }

    input.suspended = settingsOpen || gameOverOpen || session.betweenActs;

    /*
     * Last, because everything above can move a box the board is under: the
     * cash pill widens by a digit and shoves the round pill along, the wave bar
     * grows a SEND button, the tower panel opens as a drawer. A
     * `ResizeObserver` misses the first of those — the band never resizes, only
     * its contents do — and rebuilding *before* the writes leaves the mask a
     * frame stale, which is a frame in which a covered tile still reads open.
     *
     * Unconditional, rather than gated on something being armed. Gating it
     * saved nothing measurable — forty rect reads against a layout the browser
     * is about to compute anyway — and it left the mask stale for any reader
     * that is not the build cursor, which is exactly the kind of "only wrong
     * sometimes" this whole change exists to get rid of.
     */
    rebuildOcclusion();
  }

  // ── dev hook ──

  /** Wipes the board without restarting the run counters. */
  function clearBoard(): void {
    resetEnemies(world);
    world.towers.length = 0;
    world.projectiles.length = 0;
    world.pending.length = 0;
    world.fx.particles.length = 0;
    world.fx.shockwaves.length = 0;
    world.fx.numbers.length = 0;
    world.fx.shake = 0;
    particles.clear();
    trails.clear();
    grid.reset();
    floaters.clear();
    selected = null;
    armed = null;
    renderer.clearHistory();
  }

  const ctx: scenarios.ScenarioContext = {
    world,
    stepOnce(): void {
      simStep();
      refreshUi();
      renderFrame(0, SIM.dt);
    },
    place(archetypeId, x, y, tiers): void {
      const arch = towerById(archetypeId);
      const t = createTower(arch, snap(x), snap(y));
      if (tiers) {
        t.tiers[0] = tiers[0];
        t.tiers[1] = tiers[1];
        t.tiers[2] = tiers[2];
        t.stats = applyUpgrades(arch, t.tiers);
        collectGrants(arch, t.tiers, t.grants);
        t.invested = totalInvested(arch, t.tiers);
      }
      world.towers.push(t);
    },
    spawn(archetypeId, n, x, y): void {
      const arch = enemyById(archetypeId);
      for (let i = 0; i < n; i++) {
        // Fan them along the track so a swarm arrives as a cloud, not a column.
        const at = (i / Math.max(1, n)) * Math.min(track.length * 0.55, 420);
        const e = acquire(arch, world, at, (world.rng() * 2 - 1) * 0.9);
        if (!e) break;
        if (x !== undefined && y !== undefined) {
          e.pos.x = x + (world.rng() * 2 - 1) * 24;
          e.pos.y = y + (world.rng() * 2 - 1) * 24;
        }
        e.birth = 1;
      }
    },
    shock(x, y, strength, radius): void {
      world.fx.shockwaves.push({ x, y, strength, radius, duration: 0 });
    },
    setPhase(p): void {
      world.phase = p;
    },
    enterAct(round): void {
      const act = actOf(round);
      installActMap(act.map);
      world.round = round;
      world.phase = 'combat';
      refreshUi();
    },
    actBreak(finishedRound): void {
      installActMap(actOf(finishedRound).map);
      world.round = finishedRound;
      world.phase = 'combat';
      // Goes through `Session.checkActBoundary`, so the panel below is the one
      // a finished act really raises.
      if (!session.openIntermission(finishedRound)) {
        throw new Error(`[scenarios] round ${finishedRound} is not an act finale`);
      }
    },
    takeOffer(index): void {
      const held = session.intermission;
      if (!held) throw new Error('[scenarios] takeOffer with no intermission open');
      const offer = held.offers[index];
      if (!offer) throw new Error(`[scenarios] no offer ${index}`);
      session.chooseAbility(offer.id);
    },
  };

  registerScenarios();

  /**
   * The hook, widened by one accessor the capture harness needs and the game
   * does not. Declared as a variable rather than inline so the extra member
   * survives the excess-property check on `install`'s parameter.
   */
  const devHook: scenarios.DevHook & {
    placementMaskFor(id: string): Uint8Array;
  } = {
    ready: false,
    world,
    scenario(name: string): void {
      captureMode = true;
      session.start();
      clearBoard();
      scenarios.run(name, ctx);
      refreshUi();
    },
    advanceFrames(n: number): void {
      captureMode = true;
      for (let i = 0; i < n; i++) {
        simStep();
        refreshUi();
        renderFrame(0, SIM.dt);
      }
    },
    list: () => scenarios.names(),
    post(key: string, value: number): void {
      renderer.post.set(key as keyof PostParams, value as never);
    },
    resume(): void {
      captureMode = false;
      clock.reset(performance.now());
    },
    /**
     * The mask the overlay paints for `id`: the corridor, minus the footprints
     * already standing, minus whatever the chrome covers.
     *
     * `tools/uiprobe.mjs` asserts every open byte here is a tile a real click
     * reaches and builds on. That equality is the contract a swallowed click
     * breaks, and `world.buildable` alone cannot express it — it knows about
     * the map and nothing about the HUD floating over it.
     */
    placementMaskFor(id: string): Uint8Array {
      buildPlacementMask(towerById(id));
      return placementMask;
    },
  };

  scenarios.install(devHook);

  /**
   * Parks the build cursor on the nearest tile that gives the wanted answer.
   *
   * Capture-harness support only. A scenario that hard-codes a coordinate is
   * photographing whatever the map happens to put there, which is fine right up
   * until somebody moves the corridor and the "valid placement" shot quietly
   * starts showing an invalid one.
   */
  function aimAt(id: string, x: number, y: number, wantValid: boolean): void {
    let bestD2 = Infinity;
    for (let r = 0; r < VIEW.rows; r++) {
      for (let c = 0; c < VIEW.cols; c++) {
        const tx = (c + 0.5) * VIEW.tile;
        const ty = (r + 0.5) * VIEW.tile;
        if (placementLegal(id, tx, ty) !== wantValid) continue;
        const d2 = (tx - x) * (tx - x) + (ty - y) * (ty - y);
        if (d2 >= bestD2) continue;
        bestD2 = d2;
        cursor.x = tx;
        cursor.y = ty;
      }
    }
    cursorInside = bestD2 < Infinity;
  }

  function registerScenarios(): void {
    scenarios.register('empty', (c) => {
      c.setPhase('prep');
      void c;
    });

    scenarios.register('default', (c) => {
      c.setPhase('combat');
      // A mid-early round, so the wave bar has a real composition to draw
      // rather than the empty round-zero state.
      c.world.round = 8;
      c.place('turret', 200, 200, [2, 0, 0]);
      c.place('flak', 330, 240, [0, 2, 0]);
      c.place('laser', 440, 130, [1, 0, 0]);
      c.spawn('grunt', 14);
      c.spawn('runner', 6);
      c.spawn('swarmling', 40);
    });

    scenarios.register('swarm', (c) => {
      c.setPhase('combat');
      c.world.round = 24;
      c.place('pylon', 210, 190, [3, 0, 0]);
      c.place('cryo', 400, 250, [0, 3, 0]);
      c.spawn('swarmling', 260);
      c.spawn('swarmling.mite', 160);
      c.spawn('swarmling.hornet', 40);
      // A flock only reads as alive once it has been steered for a while.
      for (let i = 0; i < 120; i++) c.stepOnce();
    });

    scenarios.register('combat', (c) => {
      c.setPhase('combat');
      c.world.round = 42;
      const spots: [number, number][] = [
        [176, 176], [208, 128], [256, 208], [304, 160], [352, 240], [400, 112],
        [432, 192], [464, 272], [512, 144], [544, 224], [128, 240], [576, 96],
      ];
      const kit: [string, [number, number, number]][] = [
        ['turret', [4, 2, 0]], ['flak', [0, 4, 2]], ['laser', [5, 0, 2]],
        ['tesla', [2, 4, 0]], ['acid', [0, 2, 4]], ['sniper', [4, 0, 2]],
      ];
      spots.forEach(([x, y], i) => {
        const [id, tiers] = kit[i % kit.length];
        c.place(id, x, y, tiers);
      });
      c.spawn('grunt', 40);
      c.spawn('armoured', 14);
      c.spawn('swarmling', 90);
      c.spawn('weaver', 12);
      for (let i = 0; i < 90; i++) c.stepOnce();
    });

    scenarios.register('shockwave', (c) => {
      c.setPhase('combat');
      c.world.round = 15;
      c.spawn('grunt', 8);
      c.shock(VIEW.width * 0.5, VIEW.height * 0.5, 260, 200);
      c.shock(VIEW.width * 0.28, VIEW.height * 0.62, 180, 130);
      // Photographed mid-travel: the ring has left the epicentre but has not
      // reached the pinned border yet.
      for (let i = 0; i < 9; i++) c.stepOnce();
    });

    scenarios.register('boss', (c) => {
      c.setPhase('combat');
      c.world.round = 40;
      c.place('sniper', 208, 144, [5, 2, 0]);
      c.place('singularity', 400, 224, [0, 3, 2]);
      c.place('mortar', 296, 96, [3, 0, 2]);
      c.spawn('boss.colossus', 1);
      c.spawn('armoured', 10);
      c.spawn('swarmling', 60);
      for (let i = 0; i < 60; i++) c.stepOnce();
    });

    /**
     * The build cursor on ground that will take it. The corridor washes red on
     * its own, so one frame carries both halves of the answer.
     *
     * The tile is searched for rather than written down: a map revision that
     * moved the corridor would otherwise turn this shot red without anybody
     * noticing it had stopped photographing what it is named for.
     */
    scenarios.register('placement', (c) => {
      c.setPhase('prep');
      c.world.round = 12;
      c.place('turret', 208, 128, [1, 0, 0]);
      c.place('flak', 352, 240, [0, 1, 0]);
      c.spawn('grunt', 6);
      world.cash = 1400;
      armed = 'laser';
      aimAt('laser', VIEW.width * 0.44, VIEW.height * 0.52, true);
    });

    /** The same cursor, dropped onto the corridor. Everything turns red. */
    scenarios.register('placement-blocked', (c) => {
      c.setPhase('prep');
      c.world.round = 12;
      c.place('turret', 208, 128, [1, 0, 0]);
      c.spawn('grunt', 6);
      world.cash = 1400;
      armed = 'laser';
      aimAt('laser', VIEW.width * 0.44, VIEW.height * 0.52, false);
    });

    // ── acts ──
    //
    // Four shots that between them answer the question docs/BALANCE.md §7.7
    // asks: does the act structure exist in the running game. Each drives the
    // real `Session` path rather than posing the UI.

    /** Act I's shelf: three towers live, seven greyed with a reason. */
    scenarios.register('act-one-shelf', (c) => {
      c.enterAct(3);
      c.setPhase('prep');
      c.place('turret', 176, 128, [2, 0, 0]);
      c.place('mortar', 288, 208, [0, 1, 0]);
      world.cash = 2600;
      c.spawn('grunt', 5);
    });

    /**
     * The round *before* the intermission: act I's last, in prep, with a board
     * standing and SEND up — which is the widest the bottom band ever gets and
     * therefore the honest test of the act readout's finale state.
     */
    scenarios.register('act-finale', (c) => {
      // One short of the last round, in prep: the round the player is *about*
      // to send is the finale, which is the state the readout has to mark.
      // During prep the round counter names the round already cleared.
      c.enterAct(ACTS[0].last - 1);
      c.setPhase('prep');
      c.place('turret', 176, 128, [2, 1, 0]);
      c.place('flak', 288, 208, [0, 2, 0]);
      c.place('mortar', 352, 96, [1, 0, 1]);
      world.cash = 1400;
      c.spawn('grunt', 6);
    });

    /** The intermission itself, with a board standing behind it to refund. */
    scenarios.register('act-break', (c) => {
      c.enterAct(ACTS[0].last);
      c.place('turret', 176, 128, [3, 1, 0]);
      c.place('flak', 288, 208, [0, 3, 0]);
      c.place('mortar', 352, 96, [2, 0, 1]);
      world.cash = 900;
      c.actBreak(ACTS[0].last);
    });

    /**
     * The frame after the pick: vortex installed, board empty, act II's five
     * towers on the shelf and a full prep window to spend.
     */
    scenarios.register('act-two-open', (c) => {
      c.enterAct(ACTS[0].last);
      c.place('turret', 176, 128, [3, 1, 0]);
      c.place('flak', 288, 208, [0, 3, 0]);
      c.actBreak(ACTS[0].last);
      c.takeOffer(0);
      c.setPhase('prep');
      c.place('tesla', 320, 128, [1, 0, 0]);
      c.spawn('swarmling', 24);
    });

    /** The far end of the schedule: maelstrom, ten towers, act VII's roster. */
    scenarios.register('act-seven', (c) => {
      c.enterAct(ACTS[6].first);
      const spots: [number, number][] = [
        [200, 96], [312, 88], [440, 96], [168, 216], [352, 232],
        [488, 216], [232, 288], [408, 296], [536, 152], [120, 152],
      ];
      const kit: [string, [number, number, number]][] = [
        ['sniper', [5, 2, 0]], ['singularity', [0, 4, 2]], ['laser', [4, 0, 2]],
        ['pylon', [3, 2, 0]], ['acid', [0, 3, 3]], ['tesla', [4, 2, 0]],
      ];
      spots.forEach(([x, y], i) => {
        const [id, tiers] = kit[i % kit.length];
        c.place(id, x, y, tiers);
      });
      world.cash = 9000;
      c.spawn('swarmling.drone', 90);
      c.spawn('armoured.citadel', 6);
      for (let i = 0; i < 90; i++) c.stepOnce();
    });

    scenarios.register('ui', (c) => {
      c.setPhase('prep');
      c.world.round = 33;
      // Tier 5 on one path plus tier 2 on a second is the legal maximum, and it
      // is the state the panel has the most to say about.
      c.place('laser', 304, 176, [5, 2, 0]);
      c.spawn('grunt', 5);
      world.cash = 4200;
      select(world.towers[world.towers.length - 1] ?? null);
    });

    /*
     * The other half of the panel, and the half a player sees first: a tower
     * with nothing bought, so all three buy rows are live and every pip is a
     * price rather than a banner. `ui` is the terminal state — one path maxed,
     * one crossed over, one shut — and a sweep that only ever opened that one
     * never measured a `path__buy` at all.
     *
     * Cash sits between the tier-1 prices and the tier-2s, so the sweep sees an
     * affordable row and an unaffordable one at the same time.
     */
    scenarios.register('ui-fresh', (c) => {
      c.setPhase('prep');
      c.world.round = 4;
      c.place('turret', 304, 176);
      c.spawn('grunt', 3);
      world.cash = 100;
      select(world.towers[world.towers.length - 1] ?? null);
    });
  }

  // ── boot calibration ──

  /**
   * What the machine can do, measured before the first round rather than
   * discovered during it.
   *
   * `core/bench.ts` owns the measurement and the verdict; this owns the wiring,
   * which is four things. It renders the probe's frames through the real path, so
   * what gets measured is the frame the game actually draws. It applies the
   * verdict through `applySettings`, the same door the settings sheet uses, so
   * there is one way a tier is ever set. It hands the opening rung to the
   * governor, which owns every decision from that point on.
   *
   * And it splits the two halves by what they cost. Reading a stored verdict is
   * a `localStorage` lookup, so that happens in `boot` before the first frame is
   * drawn. Taking a fresh measurement is not free — 8ms of it on this machine,
   * 200 on a software rasteriser — so it waits until one frame has been presented
   * and runs at the top of the next. First paint is never held up by it, and the
   * one frame that renders before the verdict lands is an empty prep board.
   */

  /** `?bench=off` skips it entirely; `?bench=force` ignores a stored verdict. */
  const benchFlag = new URLSearchParams(location.search).get('bench') ?? '';

  /** Unmasked GL renderer, for the store's device signature. '' when hidden. */
  const gpuName = ((): string => {
    try {
      const gl2 = renderer.gl.getContext() as WebGL2RenderingContext;
      const info = gl2.getExtension('WEBGL_debug_renderer_info');
      if (info === null) return '';
      return String(gl2.getParameter(info.UNMASKED_RENDERER_WEBGL) ?? '');
    } catch {
      return '';
    }
  })();

  /** One pixel, read back to stall until the GPU has finished the frame. */
  const syncPixel = new Uint8Array(4);

  /**
   * True while the probe is driving the tier, so `applySettings` does not record
   * the probe's own candidates as choices the player made.
   */
  let calibrating = false;

  const benchHost: BenchHost = {
    hdr: renderer.stats.hdr,
    gpu: gpuName,
    frame(): void {
      // The render half of the loop's frame, with the sim standing still. Every
      // pass the player pays for is in here; nothing that advances the game is.
      renderFrame(0, SIM.dt);
    },
    sync(): void {
      // `readPixels` is the one stall every driver honours. The governor's timer
      // query is better and needs several frames to come back, and the probe has
      // no frames to spare.
      const gl2 = renderer.gl.getContext() as WebGL2RenderingContext;
      renderer.gl.setRenderTarget(null);
      gl2.readPixels(0, 0, 1, 1, gl2.RGBA, gl2.UNSIGNED_BYTE, syncPixel);
    },
    setTier(tier: QualityTier): void {
      renderer.setQuality(tier);
      particles.density = QUALITY[tier].particleScale;
    },
    pixels(): number {
      return renderer.stats.outputWidth * renderer.stats.outputHeight;
    },
  };

  /** Applies a verdict and hands the governor its opening rung. */
  function adopt(verdict: BenchVerdict): void {
    calibrating = true;
    applySettings({ ...settingsValues, quality: verdict.tier });
    settings.update({ open: settingsOpen, values: settingsValues });
    calibrating = false;
    // After `applySettings`, because `Renderer.setQuality` resets the ladder.
    perf.armFrom(verdict.rung);
  }

  /** Set when a measurement is owed. Taken at the top of the second frame. */
  let measureOwed = false;

  /** Applies a stored verdict now, or books a measurement for after first paint. */
  function calibrate(): void {
    if (benchFlag === 'off') return;
    try {
      const held = benchFlag === 'force'
        ? null
        : storedVerdict(gpuName, benchHost.hdr, perf.budgetMs, benchHost.pixels());
      if (held !== null) {
        adopt(held);
        return;
      }
      measureOwed = true;
    } catch (err) {
      console.warn('[main] boot benchmark skipped:', err);
    }
  }

  /** The expensive half. Called once after first paint, and from Settings. */
  function remeasure(forget_: boolean): void {
    measureOwed = false;
    try {
      if (forget_) forget(gpuName, benchHost.hdr);
      const sample = measure(benchHost, perf.budgetMs * 0.5);
      saveSample(sample);
      const fresh = verdictFor(sample, perf.budgetMs, true);
      adopt(fresh);
      /*
       * The one thing the probe says out loud, and only when it took something.
       *
       * Invisible was the alternative for the case where a concession was made,
       * and it is the wrong one: the tier moved on its own, and the sheet the
       * player would go and check shows a tier they never picked. A cabinet that
       * self-tests on boot and reports the result is in keeping with the rest of
       * this one. So: one toast, once per machine — a stored verdict says nothing
       * — with no scrim, no progress bar and nothing to dismiss. A *calibrating
       * screen* would be a loading screen, which is the thing to avoid.
       *
       * Silent when the answer is full quality at rung 0, because then there is
       * nothing to report and the toast queue is for the game.
       */
      if (fresh.tier !== 'ultra' || fresh.rung > 0) {
        const label = fresh.tier === 'ultra' ? 'Ultra' : fresh.tier === 'standard' ? 'Mid' : 'Low';
        const trim = fresh.rung > 0 ? `, ${fresh.rung} step${fresh.rung === 1 ? '' : 's'} down` : '';
        toast.push({ id: 'bench', kind: 'info', text: `Calibrated · ${label} quality${trim}` });
      }
    } catch (err) {
      // A machine the probe cannot measure gets exactly today's behaviour.
      console.warn('[main] boot benchmark skipped:', err);
      calibrating = false;
    }
  }

  // ── start ──

  applySettings(settingsValues);
  calibrate();
  session.start();
  refreshUi();
  audio.setMusicState('menu');

  clock.reset(performance.now());
  raf = requestAnimationFrame(frame);

  const hook = window.__pixeltd;
  if (hook) hook.ready = true;

  if (usingStubWaves) {
    console.info('[main] round pacing is the temporary driver in game/Session.ts — Waves.ts has not landed.');
  }

  window.addEventListener('pagehide', () => {
    cancelAnimationFrame(raf);
    input.detach();
    // Owns a second WebGL context, so it has to go back explicitly.
    hydra.dispose();
  });
}

boot();
